import React, { useMemo } from "react";

import classnames from "classnames";
import arrayMutators from "final-form-arrays";
import createDecorator from "final-form-focus";
import { debounce } from "lodash";
import { Form } from "react-final-form";
import { FieldArray } from "react-final-form-arrays";
import { OnChange } from "react-final-form-listeners";
import { useDispatch, useSelector } from "react-redux";
import _ from "underscore";
import { MAX_AGE, formatAge } from "Utils/Formatters";
import { validateRequired, validateNumber, validateZero, validateMin, composeValidators } from "Utils/Validations";
import { v4 as uuid } from "uuid";

import { overallError, employeeProfileOverallError } from "../Selectors";
import { Body2 } from "Cobalt/BodyText";
import CobaltButton from "Cobalt/Button";
import { Dropdown, Option } from "Cobalt/Dropdown";
import { Table, TR, TD, SnugTD, NestedTR } from "Cobalt/TableV2";
import Button from "Common/Button";
import { replaceEmployeeProfile } from "Common/EnterAges/Actions";
import { formatEmployeeProfileAsInitialValues, formatFormValuesAsEmployeeProfile } from "Common/EnterAges/Components/Helpers/FormHelpers";
import { SIFinalFormFieldNoCol, SIFormInput } from "Common/forms";
import { Div } from "Common/HTML";
import Icon from "Common/Icon";
import { SizeSwitch } from "Common/Responsive";

import makeBem from "Utils/makeBem";

const bem = makeBem("EnterAgesComponent");
const focusOnErrors = createDecorator();
const FORM_NAME = "enter_ages";
const is18OrOlder = composeValidators(validateRequired, validateNumber, validateMin(18));
const isZeroOrOlder = composeValidators(validateRequired, validateNumber, validateZero);
const delayedReplaceEmployeeProfile = debounce((dispatch, values) => {
  dispatch(replaceEmployeeProfile(formatFormValuesAsEmployeeProfile(values)));
}, 300);

const AddFamilyButton = ({ employee, employeeIndex, updateRow, condensed }) => {
  const addNewDependent = (newDependentType) => {
    const { dependents } = employee;

    if (newDependentType === "spouse") {
      return [
        {
          temp_id: uuid(),
          type: "Spouse",
          age: null,
          autoFocus: true,
        },
        ...dependents,
      ];
    }

    return [
      ...dependents,
      {
        temp_id: uuid(),
        type: "Child",
        age: null,
        autoFocus: true,
      },
    ];
  };

  const dependentsIncludeSpouse = employee.dependents.find((dep) => dep.type === "Spouse");
  return (
    <Dropdown
      toggle={
        <Button className={bem("AddFamilyButton")} color="link" px={1}>
          {condensed ? "Add" : <SizeSwitch xs="Family" sm="Add Family Member" />}
        </Button>
      }
      onSelect={(type) => {
        if (!(dependentsIncludeSpouse && type === "spouse")) {
          updateRow(FORM_NAME, employeeIndex, {
            ...employee,
            dependents: addNewDependent(type),
          });
        }
      }}
    >
      <Option value="spouse" disabled={dependentsIncludeSpouse}>
        Add Spouse
      </Option>
      <Option value="child">Add Child</Option>
    </Dropdown>
  );
};

const RemoveDependentButton = ({ employee, employeeIndex, dependentTempId, updateRow }) => (
  <CobaltButton
    aria-label="Remove Dependent"
    color="ghost"
    circle
    size="xs"
    onClick={() => {
      updateRow(FORM_NAME, employeeIndex, {
        ...employee,
        dependents: employee.dependents.filter((d) => d.temp_id !== dependentTempId),
      });
    }}
  >
    <Icon icon="times" flavor="regular" className={bem(null, "red")} />
  </CobaltButton>
);

const RemoveEmployeeButton = ({ employeeIndex, removeEmployee }) => (
  <CobaltButton aria-label="Remove Employee" color="ghost" size="xs" circle onClick={() => removeEmployee(FORM_NAME, employeeIndex)}>
    <Icon icon="times" flavor="regular" className={bem(null, "red")} />
  </CobaltButton>
);

const DisplayErrorMessage = ({ errors, initialError, touched, overallError, limit, showAddButton, openModal }) => {
  // Ok, this is a bit of a mess. The goal here is to display the error message if:
  // 1. There is an initial error message and it is still present (we want to make sure any saved records with errors have their errors displayed)
  // 2. There are final form errors and the specific field with the error has been touched
  // 3. The record with the error is not displayed because of the limit (i.e. directly on the quote page, not in the modal)
  // 4. The error is in the foreground (i.e. not in the background behind the modal)

  const noFinalFormErrors = _.isEmpty(errors);

  const getErrorTouchedIndex = (currentError) => {
    const errorMessage = currentError?.errorMessage;
    const eeErrorIndex = currentError?.eeErrorIndex;
    const eeDependentErrorIndex = currentError?.eeDependentErrorIndex;

    if (errorMessage !== null) {
      if (eeErrorIndex !== null) {
        if (eeDependentErrorIndex !== null) {
          return `enter_ages[${eeErrorIndex}].dependents[${eeDependentErrorIndex}].age`;
        }
        return `enter_ages[${eeErrorIndex}].age`;
      }
    }
    return null;
  };

  const initialErrorStillPresent = initialError === overallError;
  const errorMessage = overallError?.errorMessage;
  const errorRecordNotDisplayed = limit && overallError?.eeErrorIndex && limit < overallError.eeErrorIndex + 1;
  const backgroundErrorMessage = openModal && !showAddButton;
  const errorTouchedIndex = getErrorTouchedIndex(overallError);
  const errorTouched = errorTouchedIndex ? touched[errorTouchedIndex] : false;

  const displayErrorMessage =
    (initialErrorStillPresent && errorMessage) ||
    (!noFinalFormErrors && errorTouched && errorMessage) ||
    (errorRecordNotDisplayed && errorMessage && !backgroundErrorMessage);

  if (displayErrorMessage) {
    return (
      <Div mt={2} mb={2}>
        <Body2 className={bem(null, "red")}>{errorMessage}</Body2>
      </Div>
    );
  }
  return (
    <Div mt={2} mb={2}>
      <Body2 className={bem(null, null)}>&nbsp;</Body2>
    </Div>
  );
};

const EnterAgesTable = ({ values, dirty, touched, errors, form: { mutators }, limit, showAddButton, condensed, initialError, openModal }) => {
  const dispatch = useDispatch();
  const overallError = useSelector(employeeProfileOverallError);
  return (
    <form>
      <OnChange name={FORM_NAME}>
        {(values) => {
          if (dirty) {
            delayedReplaceEmployeeProfile(dispatch, values);
          }
        }}
      </OnChange>
      <Div className={showAddButton ? "h-[45vh] overflow-y-auto" : ""}>
        <FieldArray name={FORM_NAME}>
          {({ fields }) => (
            <Table>
              <tbody>
                {fields
                  .map((name, eeIndex) => {
                    const employee = values[FORM_NAME][`${eeIndex}`];
                    const { dependents } = employee;
                    return (
                      <React.Fragment key={employee.temp_id}>
                        <TR withBorder={dependents.length === 0 && eeIndex < (limit || fields.length) - 1}>
                          <TD width="200px">
                            <Body2 htmlFor={employee.name} m={0} componentClass="label">
                              {employee.name}
                            </Body2>
                          </TD>
                          <SnugTD>
                            <div className={bem("AgeFormField")}>
                              <SIFinalFormFieldNoCol
                                id={employee.name}
                                name={`${name}.age`}
                                component={SIFormInput}
                                validate={is18OrOlder}
                                parse={formatAge}
                                format={formatAge}
                                type="number"
                                componentProps={{
                                  placeholder: "Age",
                                  min: 18,
                                  max: MAX_AGE,
                                }}
                                hideErrorText
                                autoFocus={employee.autoFocus}
                              />
                            </div>
                          </SnugTD>
                          <TD align="right">
                            <AddFamilyButton employee={employee} employeeIndex={eeIndex} updateRow={mutators.update} condensed={condensed} />
                          </TD>
                          <SnugTD>
                            <RemoveEmployeeButton employeeIndex={eeIndex} removeEmployee={mutators.remove} />
                          </SnugTD>
                        </TR>
                        {dependents.length > 0 &&
                          dependents.map((dep, depIndex) => (
                            <NestedTR key={dep.temp_id}>
                              <TD>
                                <Body2 htmlFor={dep.type} m={0} componentClass="label">
                                  {dep.type}
                                </Body2>
                              </TD>
                              <SnugTD>
                                <div className={bem("AgeFormField")}>
                                  <SIFinalFormFieldNoCol
                                    id={dep.type}
                                    name={`${name}.dependents[${depIndex}].age`}
                                    component={SIFormInput}
                                    validate={dep.type === "Spouse" ? is18OrOlder : isZeroOrOlder}
                                    type="number"
                                    format={formatAge}
                                    parse={formatAge}
                                    componentProps={{
                                      placeholder: "Age",
                                      min: 0,
                                      max: MAX_AGE,
                                    }}
                                    hideErrorText
                                    autoFocus={dep.autoFocus}
                                  />
                                </div>
                              </SnugTD>
                              <TD />
                              <SnugTD>
                                <RemoveDependentButton employee={employee} employeeIndex={eeIndex} dependentTempId={dep.temp_id} updateRow={mutators.update} />
                              </SnugTD>
                            </NestedTR>
                          ))}
                      </React.Fragment>
                    );
                  })
                  .slice(0, limit)}
              </tbody>
            </Table>
          )}
        </FieldArray>
      </Div>
      <DisplayErrorMessage
        touched={touched}
        errors={errors}
        initialError={initialError}
        overallError={overallError}
        limit={limit}
        showAddButton={showAddButton}
        openModal={openModal}
      />
      {showAddButton && (
        <Button
          mt={3}
          mb={3}
          color="gray"
          data-test="AddNewEmployee"
          fullWidth={condensed}
          onClick={() => {
            mutators.push(FORM_NAME, {
              temp_id: uuid(),
              name: `Employee ${values[FORM_NAME] ? values[FORM_NAME].length + 1 : 1}`,
              age: null,
              dependents: [],
              autoFocus: true,
            });
          }}
        >
          <Icon icon="plus" mr={2} />
          Add Employee
        </Button>
      )}
    </form>
  );
};

const EnterAgesTableWrapper = ({ limit, showAddButton, condensed }) => {
  const openModal = useSelector((state) => state.openModal);
  const employeeProfile = useSelector((state) => state.employeeProfile);
  const initialValues = useMemo(() => {
    return formatEmployeeProfileAsInitialValues(employeeProfile).map((employee) => {
      const dependents = (employee.dependents || []).map((dep) => ({
        ...dep,
        temp_id: dep.temp_id || uuid(),
      }));

      return {
        ...employee,
        temp_id: employee.temp_id || uuid(),
        dependents,
      };
    });
  }, [openModal]); // eslint-disable-line react-hooks/exhaustive-deps

  const initialOverallError = useSelector(overallError);
  const initialError = useMemo(() => initialOverallError, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Form
      initialValues={{
        [FORM_NAME]: initialValues,
      }}
      decorators={[focusOnErrors]}
      mutators={{ ...arrayMutators }} // onSubmit prop is required by final form but not needed for our purposes
      onSubmit={(_values) => undefined}
    >
      {(props) => (
        <EnterAgesTable limit={limit} showAddButton={showAddButton} condensed={condensed} initialError={initialError} openModal={openModal} {...props} />
      )}
    </Form>
  );
};

export default EnterAgesTableWrapper;
